import { $meetingHubProviderKey } from '@core/_globals/types/providerKeys';
import { SupportedLanguages } from '@core/_globals/types/sharedTypes';
import { ICustomerMoveToSecureRoomPayload, IJoinMeetingMessage, IMeetingUser, IScrollEventData } from '@core/_interfaces/Meetings/MeetingTypes';
import FIMenu from '@core/classes/FIMenu';
import { DigitalSignatureData } from '@core/classes/Paperwork/DigitalSignatureData';
import PaperworkDocument from '@core/classes/Paperwork/PaperworkDocument';
import { CustomerTriggers, CustomerViews, MeetingStep, VideosSeen } from '@core/classes/SharedEnums';
import util, { EventBusCore } from '@core/services/util';
import { trackEvent } from '@core/services/util/util.appInsights';
import { HubConnection, HubConnectionBuilder, HubConnectionState, Subject } from '@microsoft/signalr';
import { App, reactive } from 'vue';

export class MeetingHubPlugin {
	subject?: Subject<any> = new Subject();
	accessKey?: string = null;
	manuallyClosed?: boolean = false;
	connected?: boolean = false;
	code?: string = '';
	myConnectionId?: string = null;
	connection?: Partial<HubConnection> = null;
	volumeLevel?: number = 0.5;
	activeStep: MeetingStep;
	activePaymentSheetSlide?: number = null;
	isSendCustomerHtmlEnabled?: boolean = false;
	isRetryingToSendMsg?: boolean = false;
	startedPromise?: Promise<void> = null;
	connectionState: HubConnectionState = HubConnectionState.Disconnected;

	// #region Main Connection Actions
	connect() {
		//IF I AM CONNECTED ALREADY, DO NOTHING
		if (!this.connected && this.connection.state === HubConnectionState.Disconnected) {
			this.startedPromise = this.connection
				.start()
				.then(() => {
					this.connected = true;
					this.connectionState = HubConnectionState.Connected;
				})
				.catch(err => {
					console.error('Failed to connect with hub', err);
					return new Promise((resolve, reject) => setTimeout(() => this.connect().then(resolve).catch(reject), 5000));
				});
		}
		return this.startedPromise;
	}

	setCode(code: string) {
		this.code = code;
	}

	resetCode() {
		this.code = '';
	}

	startConnection(code?: string) {
		this.setCode(code);
		this.activeStep = null;
		this.activePaymentSheetSlide = null;
		return this.connect();
	}

	async stopConnection(message: string) {
		this.manuallyClosed = true;
		this.activeStep = null;
		this.activePaymentSheetSlide = null;
		if (this.connected && this.connection.state !== HubConnectionState.Disconnected) {
			return await this.connection
				.stop()
				.then(() => {
					this.connected = false;
					this.manuallyClosed = false;
					this.resetCode();
					this.myConnectionId = '';
				})
				.catch(err => {
					console.error(err);
				});
		}
	}

	async fimanagerStopConnection(message: string) {
		this.manuallyClosed = true;
		if (this.connected && this.connection.state !== HubConnectionState.Disconnected) {
			return await this.connection
				.stop()
				.then(() => {
					this.connected = false;
					this.manuallyClosed = false;
					this.resetCode();
					this.myConnectionId = '';
				})
				.catch(err => {
					console.error(err);
				});
		}
	}

	subscribeToMeetingEvent(eventName: string, groupId: string, callback: (...args: any[]) => void) {
		this.connection.on(eventName, (...args: any[]) => {
			callback(...args);
		});
	}

	/**
	 * Client-side lifecycle events emitted via EventBusCore from this plugin (onreconnected, onreconnecting, etc).
	 * These are NOT SignalR server→client methods, so they must not be wired with connection.on().
	 */
	private static readonly CLIENT_LIFECYCLE_EVENTS = new Set([
		'reconnectedMeetingHub',
		'reconnectingMeetingHub',
		'disconnectedFromSignalR',
		'endMeeting',
		'BroadcastReconnect',
	]);

	/** Expects an object in the format: { EventNameA: () => {}, EventNameB: () => {}, ... } */
	bulkSubscribeToMeetingEvents(eventObj: any) {
		if (typeof eventObj !== 'object') {
			console.error('Could not bulk subscribe to events. Expected to receive an object. Instead received: ', typeof eventObj);
			return;
		}

		Object.keys(eventObj).forEach(key => {
			if (!util.isFunction(eventObj[key])) {
				console.error(`Could not subscribe to event ${key}. Provided callback was not a function.`);
				return;
			}

			if (MeetingHubPlugin.CLIENT_LIFECYCLE_EVENTS.has(key)) {
				EventBusCore.on(key, eventObj[key]);
				return;
			}

			this.connection.on(key, (...args: any[]) => {
				if (typeof eventObj[key] === 'function') {
					eventObj[key].apply(null, args);
				}
			});
		});
	}

	bulkUnsubscribeToMeetingEvents(eventObj: any) {
		if (typeof eventObj !== 'object') {
			console.error('Could not bulk unsubscribe to events. Expected to receive an object. Instead received: ', typeof eventObj);
			return;
		}

		Object.keys(eventObj).forEach(key => {
			if (!util.isFunction(eventObj[key])) {
				console.error(`Could not unsubscribe to event ${key}. Provided callback was not a function.`);
				return;
			}

			if (MeetingHubPlugin.CLIENT_LIFECYCLE_EVENTS.has(key)) {
				EventBusCore.off(key, eventObj[key]);
				return;
			}

			this.unsubscribeToGroupEvent(key);
		});
	}

	subscribeToGroupEvent(eventName: string, groupId: string, callback: (...args: any[]) => void) {
		//ON EVENT BROADCAST, WE CALL THE callback METHOD
		this.connection.on(eventName, (data: any) => {
			if (data) callback(data);
			else callback();
		});

		return this.connect()
			.then(() => this.connection.invoke('JoinGroup', groupId))
			.catch(console.error);
	}

	unsubscribeToGroupEvent(eventName: string) {
		this.connection.off(eventName);
	}

	unsubscribeToGroupEvents(groupId: string) {
		return this.connect()
			.then(() => this.connection.invoke('LeaveGroup', groupId))
			.catch(console.error);
	}

	async invokeSignalRAction(eventName: string, ...params: any[]) {
		await this.startedPromise;
		try {
			await this.connection.invoke(eventName, ...params);
		} catch (err) {
			console.error(err);
		}
	}
	// #endregion

	// #region General Actions
	async ping() {
		await this.invokeSignalRAction('Ping');
	}

	sendScrollEvent(scrollData: IScrollEventData) {
		util.debounce(this.sendScrollEventDebounced.bind(this), 100, scrollData);
	}

	async sendScrollEventDebounced(scrollData: IScrollEventData) {
		await this.invokeSignalRAction('SendScrollEvent', this.code, scrollData);
	}

	async joinMeetingRoom(joinRequest: IJoinMeetingMessage) {
		if (joinRequest.isInStoreConnection == null) joinRequest.isInStoreConnection = true;

		/* eslint-disable-next-line no-console */
		console.log('Joining Meeting Room with Props: ', joinRequest);

		await this.invokeSignalRAction('JoinMeetingRoom', { meetingRoomCode: this.code, ...joinRequest });
	}

	async allowUserIntoMeeting(meetingUser: IMeetingUser) {
		await this.invokeSignalRAction('AllowUserIntoMeeting', this.code, meetingUser.connectionId);
	}

	async inStorePreConnection(newSecureRoom: string = null, roles: string[] = null, customerPayload: ICustomerMoveToSecureRoomPayload = null) {
		await this.invokeSignalRAction('InStorePreConnection', this.code, roles, newSecureRoom, customerPayload);
	}

	async leavePreConnectionRoom(newSecureRoom: string, fimenu: FIMenu = null) {
		const oldCode = this.code;
		// if the customer is needing attention, we need to leave the pre connection room and set the code to the customer's deal number and store code
		if (fimenu?.customerNeedingAttention) {
			await this.invokeSignalRAction('LeavePreConnectionRoom', this.code);
			this.setCode(`${fimenu.dealNumber}-${fimenu.storeCode}`);
		} else {
			this.setCode(newSecureRoom);
		}
		await this.invokeSignalRAction('LeavePreConnectionRoom', oldCode);
	}
	// #endregion

	//#region FIManager Invokes
	async sendActionToCustomer(
		action: CustomerTriggers,
		fimenuStringified: string,
		selectedSignerId: string = null,
		fullName: string = null,
		firstName: string = null,
		lastName: string = null,
		roles: string[] = null,
		forceSwapUser: boolean = false,
	) {
		await this.invokeSignalRAction('SendActionToCustomer', {
			meetingRoomCode: this.code,
			action,
			fimenuStringified,
			selectedSignerId,
			fullName,
			firstName,
			lastName,
			roles,
			forceSwapUser,
		});
	}

	async saveFIMenu() {
		await this.invokeSignalRAction('SaveFIMenu', this.code);
	}

	async updateActivePaymentSheetSlide(slideIndex: number) {
		await this.invokeSignalRAction('UpdateActivePaymentSheetSlide', this.code, slideIndex);
	}

	async changeFimenuLanguage(language: SupportedLanguages) {
		await this.invokeSignalRAction('ChangeFimenuLanguage', this.code, language);
	}

	async fimanagerAlreadyConnected(fimenu: string) {
		await this.invokeSignalRAction('FIManagerAlreadyConnected', this.code, fimenu);
	}

	async disconnectParticipant(connectionId: string, shouldMeetingEnd = false) {
		await this.invokeSignalRAction('DisconnectParticipant', this.code, connectionId, shouldMeetingEnd);
	}

	async endMeeting() {
		await this.invokeSignalRAction('EndMeeting', this.code);
	}

	async reconnectParticipant(request: IJoinMeetingMessage) {
		request.isInStoreConnection = true;
		await this.invokeSignalRAction('ReconnectParticipant', { meetingRoomCode: this.code, ...request });
	}

	async sendParticipantToLobby(connectionId: string[], shouldMeetingEnd: boolean = null) {
		console.log('SendParticipantToLobby', this.code, connectionId);
		await this.invokeSignalRAction('SendParticipantToLobby', this.code, connectionId, shouldMeetingEnd);
	}

	async adjustCustomerVideoVolume(volumeLevel: number) {
		console.log('adjustCustomerVideoVolume', this.code, volumeLevel);
		await this.invokeSignalRAction('AdjustCustomerVideoVolume', this.code, volumeLevel);
	}

	async customerAlreadyConnected(role: string) {
		await this.invokeSignalRAction('customerAlreadyConnected', this.code, role);
	}

	async saveCustomerViewed(type: CustomerViews, viewedDate: Date, videoId: VideosSeen | null) {
		await this.invokeSignalRAction('SaveCustomerViewed', this.code, type, viewedDate, videoId);
	}

	async setPreferredCoverage(term: number) {
		await this.invokeSignalRAction('SetPreferredCoverage', this.code, term);
	}

	async shouldCustomerGoToPaymentSheet() {
		await this.invokeSignalRAction('ShouldCustomerGoToPaymentSheet', this.code);
	}

	async setLoanDaysToPayment(days: number) {
		await this.invokeSignalRAction('SetLoanDaysToPayment', this.code, days);
	}

	async setDraftLoanTerms(term: number, sellRate: number, buyRate: number) {
		await this.invokeSignalRAction('SetDraftLoanTerms', this.code, term, sellRate, buyRate);
	}

	async saveCustomerFImenu(fimenu: string) {
		await this.invokeSignalRAction('SaveCustomerFImenu', this.code, fimenu);
	}

	async updateActiveStep(step: MeetingStep) {
		await this.invokeSignalRAction('UpdateActiveStep', this.code, step);
	}

	async resendMessageHandler(callback: () => Promise<boolean>, time: number, retries = 3) {
		console.log('resendingMessage retry attempt: ', retries);
		if (retries <= 0) {
			console.warn('Retry limit reached');
			this.isRetryingToSendMsg = false;
			return;
		}

		let timeout = null;

		const waitForRetry = () =>
			new Promise(resolve => {
				timeout = setTimeout(resolve, time);
			});

		try {
			await waitForRetry();
			const success = await callback(); // Expect callback to return true/false
			if (success) {
				console.log('Message sent successfully, exiting retries.');
				return; // Exit retries on success
			}
		} finally {
			clearTimeout(timeout);
		}

		// Exponential backoff
		// const nextDelay = time * 2;
		const nextDelay = time;

		console.log(`Retrying in ${nextDelay}ms...`);

		// Update retries by decrementing its value
		await this.resendMessageHandler(callback, nextDelay, retries - 1); // Reduce retries count
	}

	async paperworkSigned(pdfIndex: number, document: PaperworkDocument, currentSignerId: string = null) {
		console.log('Attempt to invoke.', 'pdfIndex:', pdfIndex, 'Document:', document, 'Signer:', currentSignerId, 'MeetinghubCode:', this.code);
		const docUpdateInfo = {
			document,
			meetingRoomCode: this.code,
			pdfIndex,
			currentSignerId,
		};

		try {
			await this.startedPromise;
			await this.connection.invoke('PaperworkSigned', { ...docUpdateInfo });
			return true;
		} catch (err) {
			util.logRocketTrack('Paperwork Signed Error', {});
			console.error('PAPER WORK SIGNED ERROR doc:', document.description, err);

			if (!this.isRetryingToSendMsg) {
				this.isRetryingToSendMsg = true;
				EventBusCore.emit('openResendMessageModal');
				// Await the first call to ensure retries are handled sequentially
				await this.resendMessageHandler(async () => await this.paperworkSigned(pdfIndex, document, currentSignerId), 2000, 1000);
				this.isRetryingToSendMsg = false;
				return false;
			} else {
				console.log('Retry already in progress, skipping redundant call.');
			}
		}
	}

	async fimanagerClearedSignedDocuments(currentPacketDocs: PaperworkDocument[]) {
		await this.invokeSignalRAction('FIManagerClearedSignedDocuments', this.code, currentPacketDocs);
	}

	async clearAndRestartSignedDocuments(currentPacketDocs: PaperworkDocument[], signerId: string) {
		await this.invokeSignalRAction('ClearAndRestartSignedDocuments', this.code, currentPacketDocs, signerId);
	}

	async paperworkFinished() {
		await this.invokeSignalRAction('PaperworkFinished', this.code);
	}
	async customerDisconnect() {
		await this.invokeSignalRAction('CustomerDisconnect', this.code);
	}
	async sendHTMLFromCustomer(customerScreenData: any) {
		customerScreenData.meetingRoomCode = this.code;
		await this.invokeSignalRAction('SendHTMLFromCustomer', customerScreenData);
	}

	async requestToBePresenter(connectionId: string) {
		await this.invokeSignalRAction('RequestToBePresenter', this.code, connectionId);
	}

	async grantRequestToBePresenter(connectionId: string) {
		await this.invokeSignalRAction('GrantRequestToBePresenter', this.code, connectionId);
	}

	async updateMutedStatus(connectionId: string, isMuted: boolean) {
		await this.invokeSignalRAction('UpdateMutedStatus', this.code, connectionId, isMuted);
	}

	async sendPlayVideoFromCustomer(time: number) {
		await this.invokeSignalRAction('SendPlayVideoFromCustomer', this.code, time);
	}

	async sendPauseVideoFromCustomer() {
		await this.invokeSignalRAction('SendPauseVideoFromCustomer', this.code);
	}

	async sendShowLastFrameVideo() {
		await this.invokeSignalRAction('SendShowLastFrameVideo', this.code);
	}

	async sendClientSignatureInitials(
		signerId: string,
		signature: string,
		initials: string,
		signatureId: string,
		initialsId: string,
		signatureData: DigitalSignatureData,
		initialsData: DigitalSignatureData,
		isEntitySigner: boolean = false,
	) {
		await this.invokeSignalRAction('SendClientSignatureInitials', this.code, {
			signerId,
			signature,
			initials,
			signatureId,
			initialsId,
			signatureData,
			initialsData,
			isEntitySigner,
		});
	}

	async sendDocumentIsOpen(docIndex: number, isOpen: boolean) {
		await this.invokeSignalRAction('SendDocumentIsOpen', this.code, docIndex, isOpen);
	}
	// #endregion

	//#region Cash Bash
	async adminCashBashReadyToStart() {
		await this.invokeSignalRAction('AdminCashBashReadyToStart');
	}
	async gameCashBashReadyToStart(playerConnectedCode: string) {
		await this.invokeSignalRAction('GameCashBashReadyToStart', playerConnectedCode);
	}
	async gameCashBashLeave(playerConnectedCode: string) {
		await this.invokeSignalRAction('GameCashBashLeave', playerConnectedCode);
	}
	async adminCashBashLeave() {
		await this.invokeSignalRAction('AdminCashBashLeave');
	}

	async gameCashBashConnected() {
		await this.invokeSignalRAction('GameCashBashConnected');
	}
	async gameAlreadyReady() {
		await this.invokeSignalRAction('GameAlreadyReady');
	}
	async adminSelectActivePlayer(playerId: string) {
		await this.invokeSignalRAction('AdminSelectActivePlayer', playerId);
	}
	async gameSelectCard(cardObject: any) {
		await this.invokeSignalRAction('GameSelectCard', cardObject);
	}
	async gameChangePage(page: any) {
		await this.invokeSignalRAction('GameChangePage', page);
	}
	async adminSelectCardOk(cardObject: any) {
		await this.invokeSignalRAction('AdminSelectCardOk', cardObject);
	}
	async gameUpdateActivePlayerInfo(JSONStringPlayer: string) {
		await this.invokeSignalRAction('GameUpdateActivePlayerInfo', JSONStringPlayer);
	}
	async adminUpdateBashInfo(JSONStringBash: string) {
		await this.invokeSignalRAction('AdminUpdateBashInfo', JSONStringBash);
	}
	//#endregion

	//#region Video Chat Room
	async joinVideoChatRoom() {
		await this.invokeSignalRAction('JoinVideoChatRoom', this.code);
	}

	async requestDirectConnectionSignal(signal: any) {
		await this.invokeSignalRAction('RequestDirectConnectionSignal', this.code, signal);
	}
	async acceptedInitiatorSignal(signal: any) {
		await this.invokeSignalRAction('AcceptedInitiatorSignal', this.code, signal);
	}

	//#endregion

	// #region Broadcast System
	async broadcastGroupEvent(eventName: string, groupId: string, message: string) {
		await this.invokeSignalRAction('BroadcastGroupEvent', eventName, groupId, message);
	}
	// #endregion
}

const meetingHub = reactive(new MeetingHubPlugin());

export { meetingHub };

function setupMeetingHub(app: App) {
	meetingHub.connection = reactive(
		new HubConnectionBuilder()
			.withUrl('/MeetingRoomHub', { withCredentials: false })
			//.withAutomaticReconnect([0, 2000, 10000, 30000]) yields the default behavior
			.withAutomaticReconnect({ nextRetryDelayInMilliseconds: retryContext => util.getRandomizedRetryDelay(retryContext.previousRetryCount) })
			// .configureLogging(LogLevel.Debug)
			.build(),
	);

	meetingHub.connection.onreconnected((connectionId: string) => {
		util.logRocketTrack('MeetingHub Reconnected', { meetingHubCode: meetingHub.code });
		// Track meeting reconnected in Azure Application Insights
		trackEvent('MeetingHub.Reconnected', {
			meetingCode: meetingHub.code ?? '',
			connectionId: meetingHub.myConnectionId ?? '',
		});

		console.log(
			`%c MEETING HUB %c RECONNECTED %c Connection reestablished. Connected with connectionId "${connectionId}".`,
			'background: #1e3a8a; color: #fff; font-weight: bold; padding: 2px 6px; border-radius: 3px 0 0 3px;',
			'background: #16a34a; color: #fff; font-weight: bold; padding: 2px 6px; border-radius: 0 3px 3px 0;',
			'color: #16a34a; font-weight: bold;',
		);
		meetingHub.myConnectionId = connectionId;
		meetingHub.connectionState = HubConnectionState.Connected;
		EventBusCore.emit('reconnectedMeetingHub');
	});

	meetingHub.connection.onreconnecting(error => {
		util.logRocketTrack('MeetingHub Reconnecting', { meetingHubCode: meetingHub.code });
		trackEvent('MeetingHub.Reconnecting', {
			meetingCode: meetingHub.code ?? '',
			errorName: error?.name ?? '',
			errorMessage: error?.message ?? '',
		});
		console.log(
			`%c MEETING HUB %c RECONNECTING %c Connection lost due to error "${error}". Reconnecting.`,
			'background: #1e3a8a; color: #fff; font-weight: bold; padding: 2px 6px; border-radius: 3px 0 0 3px;',
			'background: #f59e0b; color: #000; font-weight: bold; padding: 2px 6px; border-radius: 0 3px 3px 0;',
			'color: #f59e0b; font-weight: bold;',
		);
		meetingHub.connectionState = HubConnectionState.Reconnecting;
		EventBusCore.emit('reconnectingMeetingHub');
	});

	meetingHub.connection.onclose(() => {
		console.log(
			'%c MEETING HUB %c CLOSED %c Connection has been closed',
			'background: #1e3a8a; color: #fff; font-weight: bold; padding: 2px 6px; border-radius: 3px 0 0 3px;',
			'background: #dc2626; color: #fff; font-weight: bold; padding: 2px 6px; border-radius: 0 3px 3px 0;',
			'color: #dc2626; font-weight: bold;',
		);
		meetingHub.connected = false;
		meetingHub.connectionState = HubConnectionState.Disconnected;

		if (app.config.globalProperties.meetingHelper.inStore && app.config.globalProperties.meetingHelper.myRoles.includes('fimanager')) {
			app.config.globalProperties.meetingHelper.currentMeetingUsers = [];
		}

		EventBusCore.emit('BroadcastReconnect');
	});

	meetingHub.connection.on('ConnectedToSignalR', myConnectionId => {
		console.log('ConnectedToSignalR. My connectionId is: ' + myConnectionId, ' code', meetingHub.code);
		meetingHub.myConnectionId = myConnectionId;
	});
	meetingHub.connection.on('DisconnectedFromSignalR', connectionId => {
		console.log('DisconnectedFromSignalR. The disconnected users connectionid is: ' + connectionId);

		//I GOT DISCONNECTED
		if (meetingHub.myConnectionId == connectionId) {
			meetingHub.myConnectionId = null;
		}

		//EMIT THAT SOMEONE GOT DISCONNECTED (me or someone else)
		EventBusCore.emit('disconnectedFromSignalR', connectionId);
	});

	meetingHub.connection.on('SetAccessKey', accessKey => {
		meetingHub.accessKey = accessKey;
	});
	// meetingHub.connection.on('SetUserId', id => {
	// 	meetingHub.userId = id;
	// });

	//THIS SHOULD ONLY GET CALLED ON THE CUSTOMER APP
	//THIS TELLS ALL CUSTOMER PARTICIPANTS TO DISCONNECT
	meetingHub.connection.on('EndMeeting', async () => {
		console.log('Meeting Has Ended. Stopping meetingHub connection.');
		EventBusCore.emit('endMeeting');
		await meetingHub.stopConnection('F&I Manager has ended meeting for all participants');
	});
}

export function install(app: App) {
	setupMeetingHub(app);
	app.config.globalProperties.EventBus.emit('MeetingHubConnectionChanged', meetingHub.connected); // every component will use this.$meetingHub to access the event bus
	app.config.globalProperties.$meetingHub = reactive(meetingHub);
	app.provide($meetingHubProviderKey, app.config.globalProperties.$meetingHub);
}
